import { useState, useEffect, cloneElement } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PhoneField, isValidPhoneNumber } from "@/components/PhoneField";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Compass, Gem, ArrowRight, ArrowLeft, MessageCircle } from "lucide-react";

const WHATSAPP = (process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/[^\d+]/g, "");
const INVESTMENT_SIZES = ["1-3 CR", "3-5 CR", "5 CR+"];

// Shared payload posted to /api/leads. Kept small on purpose.
async function submitLead(payload) {
  await api.post("/leads", payload);
}

function GuidanceForm({ source, onDone }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", note: "Just starting my home-buying journey — please guide me." });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone || !isValidPhoneNumber(form.phone)) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name,
        phone: form.phone,
        note: form.note,
        source,
        intent: "Buy",
        path: "guidance",
      });
      toast.success("Thanks! Ayush will reach out to you shortly.");
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="guidance-form">
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        First-time buyer · No obligation · Reply within a day
      </p>
      <Input required data-testid="guidance-name" className="tool-input" placeholder="Full name" value={form.name} onChange={set("name")} />
      <PhoneField testId="guidance-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v || "" })} placeholder="Phone number" />
      <Textarea data-testid="guidance-note" className="tool-input" placeholder="What's on your mind? (optional)" value={form.note} onChange={set("note")} rows={3} />
      <Button type="submit" disabled={loading} data-testid="guidance-submit"
        className="group w-full rounded-none bg-cyan font-mono text-sm uppercase tracking-wider text-white hover:bg-cyan/90">
        {loading ? "Sending…" : "Request Callback"}
        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Button>
    </form>
  );
}

function InvestorForm({ source, onDone }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", size: INVESTMENT_SIZES[0] });

  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const openWhatsApp = (phone) => {
    const msg = encodeURIComponent(
      `Hi Ayush, I'm ${form.name || "an investor"}. I'm looking at ${form.size} range and would like to see your off-market opportunities. My number: ${phone || "—"}.`,
    );
    const number = WHATSAPP.replace(/^\+/, "");
    const url = number ? `https://wa.me/${number}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank", "noopener");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone || !isValidPhoneNumber(form.phone)) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name,
        phone: form.phone,
        source,
        intent: "Invest",
        path: "investor",
        investment_size: form.size,
        note: `Investor path — looking at ${form.size} range.`,
      });
      toast.success("You're in. Opening WhatsApp so Ayush can reach you directly.");
      openWhatsApp(form.phone);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="investor-form">
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        Off-market · Direct WhatsApp · Priority response
      </p>
      <Input required data-testid="investor-name" className="tool-input" placeholder="Full name" value={form.name} onChange={set("name")} />
      <PhoneField testId="investor-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v || "" })} placeholder="Phone number" />

      <div>
        <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Investment size</p>
        <div className="grid grid-cols-3 gap-2">
          {INVESTMENT_SIZES.map((s) => (
            <button key={s} type="button" onClick={() => set("size")(s)} data-testid={`investor-size-${s.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
              className={`rounded-none border px-3 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                form.size === s
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-transparent text-foreground hover:border-foreground"
              }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <Button type="submit" disabled={loading} data-testid="investor-submit"
        className="group w-full rounded-none bg-[#25D366] font-mono text-sm uppercase tracking-wider text-white hover:bg-[#20c15d]">
        <MessageCircle className="mr-2 h-4 w-4" />
        {loading ? "Connecting…" : "Message Ayush on WhatsApp"}
      </Button>
      <p className="text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        We&apos;ll flag your enquiry as priority the moment you tap.
      </p>
    </form>
  );
}

const CARD_CLS =
  "group flex flex-col items-start gap-3 rounded-none border-2 border-border bg-background p-6 text-left transition-all hover:-translate-y-0.5 hover:border-foreground hover:shadow-[0_10px_0_-2px] hover:shadow-foreground/10";

export function TalkToAyushDialog({ trigger, source = "talk-to-ayush", defaultOpen = false, open: openProp, onOpenChange }) {
  const [uOpen, setUOpen] = useState(defaultOpen);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : uOpen;
  const setOpen = (v) => { if (!isControlled) setUOpen(v); onOpenChange?.(v); };
  const [step, setStep] = useState("pick"); // pick | guidance | investor

  useEffect(() => { if (!open) setStep("pick"); }, [open]);

  const dialog = (
    <DialogContent className="max-w-lg border-border bg-card" data-testid="talk-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-3xl font-extrabold uppercase">
          {step === "pick" ? "Talk to Ayush" : step === "guidance" ? "I'm exploring" : "I'm an investor"}
        </DialogTitle>
        <DialogDescription className="text-muted-foreground">
          {step === "pick"
            ? "One tap. Pick the path that fits you — we'll take it from there."
            : step === "guidance"
              ? "Share two details and Ayush will get in touch."
              : "Two details and you'll be talking to Ayush on WhatsApp."}
        </DialogDescription>
      </DialogHeader>

      {step === "pick" && (
        <div className="grid gap-3 sm:grid-cols-1">
          <button className={CARD_CLS} onClick={() => setStep("guidance")} data-testid="pick-guidance">
            <div className="flex w-full items-start justify-between">
              <Compass className="h-6 w-6 text-cyan" />
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </div>
            <p className="font-display text-xl font-extrabold uppercase text-foreground">I&apos;m exploring / first-time buyer</p>
            <p className="text-sm text-muted-foreground">Get guidance, tools and honest answers.</p>
          </button>

          <button className={CARD_CLS} onClick={() => setStep("investor")} data-testid="pick-investor">
            <div className="flex w-full items-start justify-between">
              <Gem className="h-6 w-6 text-foreground" />
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </div>
            <p className="font-display text-xl font-extrabold uppercase text-foreground">I&apos;m an investor looking for deals</p>
            <p className="text-sm text-muted-foreground">Get access to exclusive, off-market opportunities.</p>
          </button>
        </div>
      )}

      {step !== "pick" && (
        <div>
          {step === "guidance" && <GuidanceForm source={source + ":guidance"} onDone={() => setOpen(false)} />}
          {step === "investor" && <InvestorForm source={source + ":investor"} onDone={() => setOpen(false)} />}
          <button onClick={() => setStep("pick")} data-testid="talk-back"
            className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
        </div>
      )}
    </DialogContent>
  );

  // Support "controlled" usage (no trigger) so the FAB can open the same dialog.
  if (isControlled && !trigger) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {dialog}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger && cloneElement(trigger)}</DialogTrigger>
      {dialog}
    </Dialog>
  );
}
